import type {
  SchedulePreferenceInput,
  ScheduleRepeats,
  SessionScheduleOption,
  SessionSchedulerPayload,
} from '../types/sessionSchedule';

const MIN_PARTIES_TO_CONFIRM = 2;

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

export function flattenParticipantsToOptions(
  payload: SessionSchedulerPayload | null | undefined,
): SessionScheduleOption[] {
  if (!payload?.participants) {
    return [];
  }
  const rows: SessionScheduleOption[] = [];
  for (const summary of Object.values(payload.participants)) {
    for (const opt of summary.options) {
      rows.push({
        option_id: opt.option_id,
        canonical_option_id: opt.canonical_option_id,
        proposed_by: summary.user,
        rank: opt.rank,
        starts_at: opt.starts_at,
        duration_minutes: opt.duration_minutes,
        is_carry_forward: Boolean(opt.is_carry_forward),
        selected_by: opt.selected_by ?? [],
        repeats: opt.repeats ?? null,
        timezone: opt.timezone ?? '',
      });
    }
  }
  rows.sort(
    (a, b) =>
      a.starts_at.localeCompare(b.starts_at)
      || (a.rank ?? 0) - (b.rank ?? 0)
      || a.option_id - b.option_id,
  );
  return rows;
}

const MAX_STUB_SESSION = 12;

/** Lesson-plan sessions when present; otherwise stub Session 1–12. */
export function sessionSelectChoices(
  payload: SessionSchedulerPayload | null | undefined,
): Array<{ key: number; session_number: number; label: string }> {
  const fromPlan = payload?.lesson_plan?.sessions;
  if (fromPlan?.length) {
    return fromPlan.map((session) => {
      const session_number = session.entity?.session_number ?? session.id;
      return {
        key: session.id,
        session_number,
        label: session.str,
      };
    });
  }
  return Array.from({ length: MAX_STUB_SESSION }, (_, i) => {
    const session_number = i + 1;
    return {
      key: session_number,
      session_number,
      label: `Session ${session_number}`,
    };
  });
}

export interface MyPreference {
  rank: number | null;
  starts_at: string;
  duration_minutes: number;
  repeats: ScheduleRepeats;
  option_id: number;
  canonical_option_id: number;
}

/** The viewer's own options (both sources), ranked ones first in rank order. */
export function myPreferencesFromOptions(
  options: SessionScheduleOption[],
  viewerUserId: number,
): MyPreference[] {
  return options
    .filter((o) => o.proposed_by?.id === viewerUserId)
    .map((o) => ({
      rank: o.rank,
      starts_at: o.starts_at,
      duration_minutes: o.duration_minutes,
      repeats: o.repeats,
      option_id: o.option_id,
      canonical_option_id: o.canonical_option_id,
    }))
    .sort(
      (a, b) =>
        (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER)
        || a.starts_at.localeCompare(b.starts_at),
    );
}

/** Ranks follow list order (1..n); every item carries the browser timezone. */
export function toPreferenceInputs(
  items: Array<Pick<MyPreference, 'starts_at' | 'duration_minutes' | 'repeats'>>,
): SchedulePreferenceInput[] {
  const timezone = browserTimeZone();
  return items.map((item, index) => ({
    rank: index + 1,
    starts_at: item.starts_at,
    duration_minutes: item.duration_minutes,
    repeats: item.repeats,
    timezone,
  }));
}

export function sameSlot(
  a: { starts_at: string; duration_minutes: number },
  b: { starts_at: string; duration_minutes: number },
): boolean {
  return (
    a.duration_minutes === b.duration_minutes
    && Math.floor(Date.parse(a.starts_at) / 1000) === Math.floor(Date.parse(b.starts_at) / 1000)
  );
}

function slotGroupKey(option: { starts_at: string; duration_minutes: number }): string {
  return `${Math.floor(Date.parse(option.starts_at) / 1000)}:${option.duration_minutes}`;
}

/** One UI row per wall-clock slot, merging rows that differ only by proposer. */
export interface GroupedScheduleSlot {
  starts_at: string;
  duration_minutes: number;
  canonical_option_id: number;
  confirm_option_id: number;
  selected_by: number[];
  is_carry_forward: boolean;
  options: SessionScheduleOption[];
}

export function groupOptionsBySlot(options: SessionScheduleOption[]): GroupedScheduleSlot[] {
  const byKey = new Map<string, SessionScheduleOption[]>();
  for (const option of options) {
    const key = slotGroupKey(option);
    const list = byKey.get(key) ?? [];
    list.push(option);
    byKey.set(key, list);
  }

  const slots: GroupedScheduleSlot[] = [];
  for (const group of byKey.values()) {
    group.sort((a, b) => a.option_id - b.option_id);
    const canonical_option_id = Math.min(...group.map((o) => o.canonical_option_id));
    const confirm_option_id =
      group.find((o) => o.option_id === canonical_option_id)?.option_id ?? group[0].option_id;
    const selectedBy = new Set<number>();
    for (const opt of group) {
      for (const userId of opt.selected_by) {
        selectedBy.add(userId);
      }
    }
    slots.push({
      starts_at: group[0].starts_at,
      duration_minutes: group[0].duration_minutes,
      canonical_option_id,
      confirm_option_id,
      selected_by: [...selectedBy].sort((a, b) => a - b),
      is_carry_forward: group.some((o) => o.is_carry_forward),
      options: group,
    });
  }

  slots.sort(
    (a, b) =>
      a.starts_at.localeCompare(b.starts_at)
      || a.duration_minutes - b.duration_minutes,
  );
  return slots;
}

/** Slots where at least one participant (not the viewer's own option row) proposed the time. */
export function groupedSlotsWithOthersProposals(
  allOptions: SessionScheduleOption[],
  myOptionIds: Set<number>,
): GroupedScheduleSlot[] {
  return groupOptionsBySlot(allOptions).filter((slot) =>
    slot.options.some((option) => !myOptionIds.has(option.option_id)),
  );
}

export function isGroupedSlotConfirmable(slot: GroupedScheduleSlot): boolean {
  return new Set(slot.selected_by).size >= MIN_PARTIES_TO_CONFIRM;
}

export function mySelectionCanonicalIds(
  options: SessionScheduleOption[],
  viewerUserId: number,
): number[] {
  const ids = new Set<number>();
  for (const option of options) {
    if (option.selected_by.includes(viewerUserId)) {
      ids.add(option.canonical_option_id);
    }
  }
  return [...ids];
}

export function uniqueSelectedUserCount(option: SessionScheduleOption): number {
  return new Set(option.selected_by).size;
}

export function isOptionConfirmable(option: SessionScheduleOption): boolean {
  return uniqueSelectedUserCount(option) >= MIN_PARTIES_TO_CONFIRM;
}

export function confirmableCanonicalOptionIds(options: SessionScheduleOption[]): number[] {
  const ids = new Set<number>();
  for (const option of options) {
    if (isOptionConfirmable(option)) {
      ids.add(option.canonical_option_id);
    }
  }
  return [...ids];
}

/** One row per confirmable canonical slot for the confirm picker. */
export function confirmChoiceOptions(
  options: SessionScheduleOption[],
): SessionScheduleOption[] {
  const seen = new Set<number>();
  const choices: SessionScheduleOption[] = [];
  for (const option of options) {
    if (!isOptionConfirmable(option)) {
      continue;
    }
    const canonicalId = option.canonical_option_id;
    if (seen.has(canonicalId)) {
      continue;
    }
    seen.add(canonicalId);
    const canonicalRow =
      options.find((o) => o.option_id === canonicalId) ?? option;
    choices.push(canonicalRow);
  }
  return choices;
}
